import React, { useState } from 'react';
import { Package, MapPin, Calendar, Clock, Phone, Mail, User, Weight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import { trackConversion, generateSubmissionId } from '../utils/googleAdsTracking';

const SchedulePickupPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    pickupAddress: '',
    postalCode: '',
    city: '',
    province: '',
    numBoxes: '',
    shipmentType: 'Boxes',
    approximateWeight: '',
    preferredDate: '',
    preferredTime: 'Morning (9AM-12PM)',
    specialInstructions: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.phoneNumber || !formData.email || 
        !formData.pickupAddress || !formData.postalCode || !formData.city || 
        !formData.province || !formData.numBoxes || !formData.approximateWeight || 
        !formData.preferredDate) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (!API_URL) {
        throw new Error('Backend API URL is not configured. Please contact support.');
      }
      const response = await fetch(`${API_URL}/api/schedule-pickup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Track Google Ads conversion (ONLY on successful submission)
        const submissionId = generateSubmissionId('pickup');
        trackConversion(submissionId, 'schedule_pickup');
        
        toast({
          title: "Success!",
          description: "Pickup scheduled successfully! We'll contact you shortly to confirm.",
          className: "bg-green-50 border-green-500"
        });
        // Reset form
        setFormData({
          fullName: '',
          phoneNumber: '',
          email: '',
          pickupAddress: '',
          postalCode: '',
          city: '',
          province: '',
          numBoxes: '',
          shipmentType: 'Boxes',
          approximateWeight: '',
          preferredDate: '',
          preferredTime: 'Morning (9AM-12PM)',
          specialInstructions: ''
        });
      } else {
        throw new Error(data.detail || 'Failed to schedule pickup');
      }
    } catch (error) {
      console.error('Schedule pickup error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to schedule pickup. Please try again or call us at +1 (437) 616-0758",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Schedule Pickup Canada | Free Door to Door Shipping | Shippzy</title>
        <meta name="description" content="Schedule free pickup for international shipping from Canada. Door to door courier service available across Toronto, Vancouver, Calgary. Fast, reliable worldwide shipping." />
        <link rel="canonical" href="https://www.shippzy.ca/schedule-pickup" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              📦 Convenient Pickup Service
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Schedule a Pickup
            </h1>
            <p className="text-xl text-gray-600">
              Fill out the form below and we'll arrange a convenient pickup time for your shipment
            </p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-6 h-6 text-red-600" />
                Pickup Request Form
              </CardTitle>
              <CardDescription>
                All fields marked with * are required
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <Input
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <Input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                        placeholder="+1 (123) 456-7890"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Pickup Address */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pickup Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Address *</label>
                      <Input
                        name="pickupAddress"
                        value={formData.pickupAddress}
                        onChange={handleChange}
                        required
                        placeholder="123 Main Street, Unit 4"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          placeholder="Toronto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Province *</label>
                        <Input
                          name="province"
                          value={formData.province}
                          onChange={handleChange}
                          required
                          placeholder="ON"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code *</label>
                        <Input
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleChange}
                          required
                          placeholder="M1A 1A1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipment Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipment Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Number of Boxes *</label>
                      <Input
                        type="number"
                        name="numBoxes"
                        value={formData.numBoxes}
                        onChange={handleChange}
                        required
                        min="1"
                        placeholder="5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Shipment Type *</label>
                      <select
                        name="shipmentType"
                        value={formData.shipmentType}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="Boxes">Boxes</option>
                        <option value="Documents">Documents</option>
                        <option value="Luggage">Luggage</option>
                        <option value="Relocation">Relocation</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Approximate Weight (kg) *</label>
                      <Input
                        type="number"
                        name="approximateWeight"
                        value={formData.approximateWeight}
                        onChange={handleChange}
                        required
                        min="1"
                        placeholder="25"
                      />
                    </div>
                  </div>
                </div>

                {/* Pickup Schedule */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferred Pickup Schedule</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                      <Input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                      <select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
                        <option value="Afternoon (12PM-3PM)">Afternoon (12PM-3PM)</option>
                        <option value="Evening (3PM-6PM)">Evening (3PM-6PM)</option>
                        <option value="Anytime">Anytime</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Special Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Instructions (Optional)</label>
                  <Textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Building access codes, parking instructions, or any special requirements..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Scheduling...' : 'Schedule Pickup'}
                </Button>

                <p className="text-sm text-center text-gray-600">
                  Need immediate assistance? Call us at <a href="tel:+14376160758" className="text-red-600 hover:text-red-700 font-semibold">+1 (437) 616-0758</a>
                </p>
              </form>
            </CardContent>
          </Card>

          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchedulePickupPage;
